import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, Menu, Play, Plus, WalletCards } from "lucide-react";
import API, { getMediaUrl } from "../services/api";
import Sidebar from "../components/Sidebar";
import BottomNav from "../components/BottomNav";
import BrandLogo from "../components/BrandLogo";
import WhatsAppGlyph from "../components/WhatsAppGlyph";
import BannerCarousel from "../components/BannerCarousel";
import TickerMarquee from "../components/TickerMarquee";
import { getMarketStatusLabel, getMarketWindowForNow, isMarketClosed } from "../utils/marketStatus";
import { initPushNotifications } from "../services/push";
import { isAppWebView } from "../utils/appWebView";
import { buildWhatsAppUrl, consumeDashboardNotice, openExternalLink } from "../utils/appInteractions";
import usePullToRefresh from "../hooks/usePullToRefresh";

function Dashboard() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [markets, setMarkets] = useState([]);
  const [banners, setBanners] = useState([]);
  const [activeBanner, setActiveBanner] = useState(0);
  const [balance, setBalance] = useState(0);
  const [settings, setSettings] = useState({});
  const [viewportWidth, setViewportWidth] = useState(() => window.innerWidth);
  const [pageNotice, setPageNotice] = useState("");

  const user = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
  const normalMarkets = useMemo(
    () => markets.filter((market) => (market.market_type || "normal") !== "starline").sort(sortMarketsByOpenTime),
    [markets]
  );
  const displayMarkets = normalMarkets.length ? normalMarkets : fallbackMarkets;
  const displayBanners = Array.isArray(banners) ? banners : [];
  const isMobile = viewportWidth <= 480;
  const isTight = viewportWidth <= 390;
  const isDesktop = viewportWidth >= 900;
  const isLaptop = viewportWidth >= 900 && viewportWidth <= 1366;
  const inAppWebView = isAppWebView();
  const starlineLogo = resolveAssetUrl(settings.asset_starline_logo_url, "/starline-card-logo-final-2.png");
  const aviatorLogo = resolveAssetUrl(settings.asset_aviator_logo_url, "/aviator_logo_5_transparent_outside_512.webp");
  const colorGameLogo = resolveAssetUrl(settings.asset_color_game_logo_url, "/background-removed-background-removed.png");

  useEffect(() => {
    const nextNotice = consumeDashboardNotice();
    if (!nextNotice) return undefined;
    setPageNotice(nextNotice);
    const timer = window.setTimeout(() => setPageNotice(""), 3200);
    return () => window.clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (user?.id) {
      fetchWallet(user.id);
      initPushNotifications(user.id).catch(() => {});
    }
  }, [user]);

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveBanner((current) => (current + 1) % Math.max(banners.length || 1, 1));
    }, 3500);

    return () => clearInterval(timer);
  }, [banners.length]);

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const fetchMarkets = useCallback(async () => {
    try {
      const res = await API.get("/markets");
      setMarkets(Array.isArray(res.data) ? res.data.slice().sort(sortMarketsByOpenTime) : []);
    } catch {
      setMarkets([]);
    }
  }, []);

  const fetchBanners = useCallback(async () => {
    try {
      const res = await API.get("/banners");
      setBanners(Array.isArray(res.data) ? res.data : []);
    } catch {
      setBanners([]);
    }
  }, []);

  const fetchWallet = async (userId) => {
    try {
      const res = await API.get(`/wallet/${userId}`);
      setBalance(Number(res.data?.balance || 0));
    } catch {
      setBalance(0);
    }
  };

  const refreshDashboard = useCallback(async () => {
    await Promise.all([
      fetchMarkets(),
      fetchBanners(),
      API.get("/settings/public").then((res) => setSettings(res.data || {})).catch(() => {}),
      user?.id ? fetchWallet(user.id) : Promise.resolve(),
    ]);
  }, [fetchBanners, fetchMarkets, user]);

  useEffect(() => {
    refreshDashboard().catch(() => {});
  }, [refreshDashboard]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      refreshDashboard().catch(() => {});
    }, 30000);
    return () => window.clearInterval(interval);
  }, [refreshDashboard]);

  const pullToRefresh = usePullToRefresh(refreshDashboard);

  const openGameSelect = (market) => {
    if (isMarketClosed(market)) {
      alert("Market closed for today");
      return;
    }

    if (!user) {
      navigate("/login");
      return;
    }

    sessionStorage.setItem("selectedMarketId", String(market.id));
    const selectedMarket = markets.find((item) => String(item.id) === String(market.id));
    if ((selectedMarket?.market_type || "normal") === "starline") {
      navigate(`/starline/${market.id}`);
      return;
    }
    navigate("/game-select");
  };

  const openWhatsapp = () => {
    const phone = getWhatsappPhone(settings.whatsapp_number);
    if (!phone) return;
    const url = buildWhatsAppUrl(phone, "Hello Matka Kings support");
    if (!url) return;
    openExternalLink(url);
  };

  return (
    <div style={page} {...pullToRefresh.bind}>
      <header style={{ ...topbar, ...(inAppWebView ? topbarApp : null) }}>
        <div style={{ ...leftBrand, ...(inAppWebView ? leftBrandApp : null) }}>
          <button type="button" style={menuButton} onClick={() => setOpen(true)}>
            <Menu size={24} />
          </button>
          <button type="button" style={brandButton} onClick={() => navigate("/dashboard")}>
            <BrandLogo
              size={inAppWebView ? "sm" : isTight ? "sm" : "md"}
              compact={false}
              gap={inAppWebView ? 1 : isTight ? 1 : 2}
              nameScale={inAppWebView ? 1.02 : isTight ? 1.05 : 1.14}
              hideIcon={false}
            />
          </button>
        </div>

        {user ? (
          <div style={topActions}>
            <button type="button" style={{ ...walletPill, ...(isMobile ? walletPillMobile : null), ...(inAppWebView ? walletPillApp : null) }} onClick={() => navigate("/wallet")}>
              <span style={{ ...walletIconBubble, ...(isMobile ? walletIconBubbleMobile : null) }}>
                <WalletCards size={isMobile ? 15 : 18} strokeWidth={2.4} />
              </span>
              <span style={{ whiteSpace: "nowrap" }}>{formatTopbarBalance(balance)}</span>
            </button>
          </div>
        ) : (
          <div style={authActions}>
            <button type="button" style={loginButton} onClick={() => navigate("/login")}>Login</button>
          </div>
        )}
      </header>

      <Sidebar isOpen={open} setIsOpen={setOpen} />

      {pageNotice ? (
        <div style={pageNoticeBanner}>
          {pageNotice}
        </div>
      ) : null}

      <main style={{ ...viewport, ...(isDesktop ? viewportDesktop : null), ...(isLaptop ? viewportLaptop : null) }}>
        <button type="button" style={{ ...heroBanner, ...(isDesktop ? heroBannerDesktop : null), ...(isLaptop ? heroBannerLaptop : null) }} onClick={() => navigate("/dashboard")}>
          <TickerMarquee
            text={String(settings.home_running_notice || "Auto Deposit | Auto Withdraw | Fast Results | Secure Wallet | Official Matka Kings Updates").trim()}
            speed={58}
            outerStyle={noticeTickerWrap}
            trackStyle={noticeTickerTrack}
            textStyle={noticeTickerText}
          />
          <BannerCarousel
            banners={displayBanners}
            activeIndex={activeBanner}
            desktop={isDesktop}
            laptop={isLaptop}
            imageStyle={heroImage}
            desktopImageStyle={heroImageDesktop}
            laptopImageStyle={heroImageLaptop}
            altPrefix="Dashboard banner"
            fallback={
              <div style={{ ...heroFallback, ...(isDesktop ? heroFallbackDesktop : null), ...(isLaptop ? heroFallbackLaptop : null) }}>
                <strong style={heroText}>Matka Kings Live Markets</strong>
                <span style={heroSub}>Daily banners from admin will show here automatically</span>
              </div>
            }
          />
        </button>

        <section style={{ ...quickActionGrid, ...(isMobile ? quickActionGridMobile : null), ...(isDesktop ? quickActionGridDesktop : null), ...(isLaptop ? quickActionGridLaptop : null) }}>
          <button type="button" style={{ ...quickAction, ...dashboardCardJump, animationDelay: "0s", ...(isMobile ? quickActionMobile : null), ...(isLaptop ? quickActionLaptop : null), background: "linear-gradient(135deg,#22c55e,#0f9f57)", border: "1px solid rgba(52,211,153,.22)" }} onClick={() => navigate(user ? "/deposit" : "/login")}>
            <span style={{ ...quickIcon, ...(isMobile ? quickIconBubbleMobile : null) }}><Plus size={isMobile ? 26 : 28} /></span>
            <strong style={quickTileLabel}>Add Money</strong>
          </button>
          <button type="button" style={{ ...gameLogoCard, ...dashboardCardJump, animationDelay: ".18s", ...starlineLogoCard, ...(isMobile ? gameLogoCardMobile : null), ...(isLaptop ? gameLogoCardLaptop : null) }} onClick={() => navigate(user ? "/starline" : "/login")}>
            <img src={starlineLogo} alt="King Starline" style={dashboardWideLogoImg} />
          </button>
          <button
            type="button"
            style={{ ...gameLogoCard, ...dashboardCardJump, animationDelay: ".36s", ...aviatorLogoCard, ...(isMobile ? gameLogoCardMobile : null), ...(isLaptop ? gameLogoCardLaptop : null), opacity: String(settings.aviator_enabled || "1") === "1" ? 1 : 0.72 }}
            onClick={() => {
              if (String(settings.aviator_enabled || "1") !== "1") return;
              navigate(user ? "/aviator" : "/login");
            }}
          >
            {String(settings.aviator_enabled || "1") !== "1" ? (
              <div style={disabledGameTile}>
                <strong style={disabledGameTitle}>Aviator</strong>
                <span style={disabledGameNote}>Off</span>
              </div>
            ) : (
              <>
              <img src={aviatorLogo} alt="Aviator" style={aviatorGameLogoImg} />
              </>
            )}
          </button>
          <button type="button" style={{ ...gameLogoCard, ...dashboardCardJump, animationDelay: ".54s", ...colorLogoCard, ...(isMobile ? gameLogoCardMobile : null), ...(isLaptop ? gameLogoCardLaptop : null) }} onClick={() => navigate(user ? "/color-game" : "/login")}>
            <img src={colorGameLogo} alt="Color Game" style={dashboardColorLogoImg} />
          </button>
        </section>

        {!inAppWebView && String(settings.app_download_url || "").trim() ? (
          <button type="button" style={dashboardDownloadCard} onClick={() => openExternalLink(String(settings.app_download_url).trim())}>
            <span style={dashboardDownloadRow}>
              <span style={dashboardDownloadIconWrap}>
                <img src="/Google_Play_2016_icon.svg" alt="" style={dashboardDownloadIconImg} />
              </span>
              <span style={dashboardDownloadTitle}>{settings.app_download_label || "Download App Now"}</span>
            </span>
          </button>
        ) : null}

        <section style={sectionHeader}>
          <h2>Live Results</h2>
        </section>

        <div style={{ ...marketList, ...(isDesktop ? marketListDesktop : null), ...(isLaptop ? marketListLaptop : null) }}>
          {displayMarkets.map((market) => {
            const marketClosed = isMarketClosed(market);
            return (
            <article key={market.id} style={marketClosed ? closedMarketCard : marketCard}>
              <button
                type="button"
                style={marketMainTap}
                onClick={() => openGameSelect(market)}
                disabled={marketClosed}
                aria-label={marketClosed ? `${market.name} market closed` : `Open ${market.name} bet menu`}
              >
                {!marketClosed ? <span aria-hidden="true" style={marketGlossSweep} /> : null}
                <div style={marketTop}>
                  <div style={marketTitleWrap}>
                    <div style={marketHeadingWrap}>
                      <strong style={marketName}>{market.name}</strong>
                      <span style={marketClosed ? closedPill : openPill}>{getMarketStatusLabel(market)}</span>
                    </div>
                  </div>
                  <div style={marketMeta}>
                    {(() => {
                      const window = getMarketWindowForNow(market);
                      return (
                        <span style={marketTime}>
                          {formatMarketTime(window.open_time || market.open_time)} - {formatMarketTime(window.close_time || market.close_time)}
                        </span>
                      );
                    })()}
                  </div>
                </div>

                <div style={marketClosed ? closedResultRow : resultRow}>
                  <b style={{ ...resultBadge, ...(hasRealResult(market.result) ? realResultTone : placeholderTone) }}>
                    {market.result || "*** - ** - ***"}
                  </b>
                  <div
                    style={marketClosed ? disabledPlayButton : playButton}
                    aria-hidden="true"
                  >
                    <Play size={24} fill="#fff" />
                  </div>
                </div>
              </button>

              <div style={chartRow}>
                <button type="button" style={chartButtonBlue} onClick={() => navigate(`/charts/market/${market.id}?view=jodi`)}>
                  <BarChart3 size={11} /> Jodi Chart
                </button>
                <button type="button" style={chartButtonPurple} onClick={() => navigate(`/charts/market/${market.id}?view=panel`)}>
                  <BarChart3 size={11} /> Panel Chart
                </button>
              </div>
            </article>
          )})}
        </div>

        <section style={dashboardTailSection}>
          <button type="button" style={{ ...tailPromoCard, ...tailPromoBlue }} onClick={() => navigate("/how-to-play")}>
            <div style={tailPromoCopy}>
              <strong style={tailPromoTitle}>How To Play Matka?</strong>
              <small style={tailPromoSub}>Simple basics and quick guide for new players</small>
            </div>
            <span style={tailPromoBadge}>Guide</span>
          </button>

          <div style={tailTrustGrid}>
            <article style={tailTrustCard}>
              <strong style={tailTrustLabel}>MK Fair Play Partner</strong>
              <span style={tailTrustValue}>Protected Markets</span>
            </article>
            <article style={tailTrustCard}>
              <strong style={tailTrustLabel}>RNG Certified View</strong>
              <span style={tailTrustValue}>Secure Result Flow</span>
            </article>
          </div>
        </section>
      </main>

      <button type="button" style={whatsappButton} onClick={openWhatsapp} aria-label="Chat on WhatsApp">
        <span style={whatsappGlyphWrap}>
          <WhatsAppGlyph size={112} color="#ffffff" />
        </span>
      </button>
      <BottomNav />
    </div>
  );
}

function getWhatsappPhone(value) {
  const digits = String(value || "").replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length === 12 && digits.startsWith("91")) return digits;
  return digits;
}

const hasRealResult = (value) => value && !String(value).includes("***");
const sortMarketsByOpenTime = (a, b) => {
  const rankA = getMarketSortRank(a);
  const rankB = getMarketSortRank(b);
  if (rankA !== rankB) return rankA - rankB;

  const timeDiff = toMarketMinutes(a.open_time) - toMarketMinutes(b.open_time);
  if (timeDiff !== 0) return timeDiff;

  const numberA = Number(String(a.market_number || a.number || a.id || a.name || "").match(/\d+/)?.[0] || 0);
  const numberB = Number(String(b.market_number || b.number || b.id || b.name || "").match(/\d+/)?.[0] || 0);
  if (numberA !== numberB) return numberA - numberB;

  return String(a.name || "").localeCompare(String(b.name || ""), undefined, { numeric: true });
};
const getMarketSortRank = (market) => {
  if (Number(market?.is_active ?? 1) === 0) return 2;
  return isMarketClosed(market) ? 1 : 0;
};
const toMarketMinutes = (value) => {
  const match = String(value || "").match(/(\d{1,2}):(\d{2})/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  return Number(match[1]) * 60 + Number(match[2]);
};

function formatTopbarBalance(value) {
  const amount = Number(value || 0);
  return `₹ ${amount.toFixed(2)}`;
}

function resolveAssetUrl(value, fallback) {
  const raw = String(value || fallback || "").trim();
  if (raw.startsWith("/uploads/")) return getMediaUrl(raw);
  return raw || fallback;
}

const formatMarketTime = (value) => {
  if (!value) return "--:--";
  const match = String(value).match(/^(\d{1,2}):(\d{2})/);
  if (!match) return String(value);
  const hour = Number(match[1]);
  const minute = match[2];
  const suffix = hour >= 12 ? "PM" : "AM";
  const displayHour = ((hour + 11) % 12) + 1;
  return `${String(displayHour).padStart(2, "0")}:${minute} ${suffix}`;
};
const fallbackMarkets = [
  { id: 1, name: "SRIDEVI NIGHT", open_time: "19:05", close_time: "20:05", result: "*** - ** - ***" },
  { id: 2, name: "MADHUR NIGHT", open_time: "20:20", close_time: "22:20", result: "*** - ** - ***" },
  { id: 3, name: "MILAN NIGHT", open_time: "20:50", close_time: "22:50", result: "*** - ** - ***" },
];

const page = { minHeight: "100vh", background: "radial-gradient(circle at top, #fff7e6 0%, #f8fafc 42%, #eef2ff 100%)", paddingBottom: "74px", color: "#0f172a", fontFamily: "Inter, system-ui, sans-serif" };
const topbar = { height: "68px", position: "fixed", left: 0, right: 0, top: 0, zIndex: 50, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 10px", background: "rgba(255,251,235,0.95)", borderBottom: "1px solid rgba(253,230,138,0.85)", boxShadow: "0 8px 22px rgba(146,64,14,0.10)", backdropFilter: "blur(12px)", boxSizing: "border-box" };
const leftBrand = { display: "flex", alignItems: "center", gap: "4px", minWidth: 0, flex: "1 1 auto", overflow: "hidden" };
const topbarApp = { padding: "0 7px", gap: "4px" };
const leftBrandApp = { gap: "2px", paddingRight: "2px" };
const brandButton = { border: 0, background: "transparent", padding: 0, display: "inline-flex", alignItems: "center", minWidth: 0, maxWidth: "100%", overflow: "hidden", cursor: "pointer" };
const menuButton = { border: 0, background: "transparent", color: "#334155", display: "grid", placeItems: "center", padding: "6px", marginLeft: "-4px", borderRadius: "8px", flexShrink: 0 };
const authActions = { display: "flex", gap: "8px" };
const loginButton = { height: "32px", border: 0, borderRadius: "18px", background: "#1d4ed8", color: "#fff", padding: "0 16px", fontWeight: 800 };
const topActions = { display: "flex", alignItems: "center", gap: "6px", flexShrink: 0 };
const walletPill = { display: "flex", alignItems: "center", gap: "7px", height: "40px", border: "1px solid #fcd34d", borderRadius: "999px", background: "linear-gradient(135deg,#fff7ed,#fffbeb)", color: "#111827", padding: "0 12px 0 6px", fontSize: "15px", fontWeight: 900, flexShrink: 0, boxShadow: "0 8px 18px rgba(245,158,11,0.14)", backdropFilter: "blur(8px)" };
const walletPillMobile = { fontSize: "11px", gap: "4px", padding: "0 7px 0 4px", minWidth: 0, height: "34px", maxWidth: "128px" };
const walletPillApp = { minWidth: "fit-content", maxWidth: "132px", marginLeft: 0 };
const walletIconBubble = { width: 26, height: 26, borderRadius: "50%", display: "grid", placeItems: "center", flexShrink: 0, color: "#92400e", background: "linear-gradient(135deg,#fde68a,#f59e0b)", boxShadow: "inset 0 1px 0 rgba(255,255,255,.55)" };
const walletIconBubbleMobile = { width: 22, height: 22 };
const viewport = { padding: "80px 12px 24px", display: "grid", gap: "12px" };
const viewportDesktop = { width: "min(100% - 48px, 1500px)", margin: "0 auto", padding: "92px 0 40px", gap: "18px" };
const viewportLaptop = { width: "min(100% - 28px, 1180px)", padding: "84px 0 34px", gap: "14px" };
const noticeTickerWrap = { width: "100%", minHeight: "34px", overflow: "hidden", borderRadius: "18px", border: "1px solid rgba(251,191,36,.24)", background: "linear-gradient(135deg,rgba(255,250,236,.82),rgba(255,255,255,.92))", boxShadow: "inset 0 1px 0 rgba(255,255,255,.82)" };
const noticeTickerTrack = { display: "inline-flex", alignItems: "center", whiteSpace: "nowrap", willChange: "transform", gap: "0" };
const noticeTickerText = { display: "inline-flex", flexShrink: 0, alignItems: "center", minHeight: "34px", padding: "0 28px", color: "#b45309", fontSize: "12px", fontWeight: 900, letterSpacing: "0.02em", textShadow: "0 1px 0 rgba(255,255,255,.8)", pointerEvents: "none" };
const heroBanner = { border: "1px solid rgba(245,158,11,.38)", width: "100%", borderRadius: "30px", overflow: "hidden", background: "linear-gradient(180deg, rgba(255,249,232,.98) 0%, rgba(255,244,214,.88) 100%)", padding: "8px", boxShadow: "0 18px 34px rgba(15,23,42,0.12), inset 0 1px 0 rgba(255,255,255,.86)", display: "grid", gap: "8px" };
const heroBannerDesktop = { borderRadius: "34px", boxShadow: "0 20px 46px rgba(15,23,42,0.12), 0 0 0 1px rgba(255,255,255,.52) inset" };
const heroBannerLaptop = { borderRadius: "24px" };
const heroImage = { width: "100%", height: "clamp(172px, 44vw, 228px)", objectFit: "contain", display: "block", borderRadius: "22px", background: "linear-gradient(135deg,rgba(255,249,240,.72),rgba(255,255,255,.92))", boxShadow: "inset 0 1px 0 rgba(255,255,255,.72)" };
const heroImageDesktop = { height: "clamp(320px, 31vw, 540px)", maxHeight: "540px", borderRadius: "28px" };
const heroImageLaptop = { height: "clamp(235px, 26vw, 360px)", maxHeight: "360px", borderRadius: "24px" };
const heroFallback = { aspectRatio: "16 / 8", maxHeight: "206px", display: "grid", placeItems: "center", background: "linear-gradient(135deg, #1d4ed8, #7c3aed)", color: "#fff", gap: "6px", borderRadius: "14px" };
const heroFallbackDesktop = { height: "clamp(320px, 34vw, 560px)" };
const heroFallbackLaptop = { height: "clamp(250px, 30vw, 390px)" };
const heroText = { fontSize: "22px", fontWeight: 800 };
const heroSub = { fontSize: "13px", opacity: 0.9 };
const quickActionGrid = { display: "grid", gridTemplateColumns: "repeat(4, minmax(0, 1fr))", gap: "8px", paddingBottom: "2px" };
const quickActionGridMobile = { gap: "7px" };
const quickActionGridDesktop = { gap: "14px" };
const quickActionGridLaptop = { gap: "10px" };
const quickAction = { minHeight: "96px", minWidth: 0, width: "100%", border: 0, borderRadius: "24px", color: "#fff", display: "grid", placeItems: "center", alignContent: "center", gap: "4px", padding: "8px", fontSize: "12px", fontWeight: 900, boxShadow: "0 12px 22px rgba(15,23,42,.12)", overflow: "hidden", position: "relative", isolation: "isolate", background: "linear-gradient(135deg,#1ecf74 0%,#12a157 52%,#0e7c44 100%)" };
const quickActionLaptop = { minHeight: "82px", borderRadius: "18px" };
const quickActionMobile = { width: "100%", minHeight: 0, height: "auto", borderRadius: "18px", padding: "7px 5px", gap: "6px", fontSize: "10px", aspectRatio: "1 / 1" };
const quickTileLabel = { fontSize: "10px", lineHeight: 1.08, textAlign: "center", fontWeight: 800, letterSpacing: 0, whiteSpace: "normal", wordBreak: "break-word", maxWidth: "100%" };
const quickIcon = { width: "42px", height: "42px", borderRadius: "50%", display: "grid", placeItems: "center", background: "rgba(255,255,255,.18)" };
const quickIconBubbleMobile = { width: "48px", height: "48px", borderRadius: "999px" };
const dashboardCardJump = { animation: "mkDashboardCardJump 5s ease-in-out infinite", transformOrigin: "center bottom", willChange: "transform" };
const gameLogoCard = { ...quickAction, position: "relative", background: "transparent", border: "1px solid rgba(255,255,255,.28)", padding: "6px", boxShadow: "0 14px 26px rgba(120,76,10,.12), inset 0 1px 0 rgba(255,255,255,.28)", gap: 0, overflow: "hidden", isolation: "isolate" };
const starlineLogoCard = { background: "radial-gradient(circle at 24% 18%, rgba(255,250,205,.38), transparent 32%), radial-gradient(circle at 78% 72%, rgba(255,200,87,.26), transparent 28%), linear-gradient(135deg,#231004 0%,#6f2f0a 24%,#bb7a19 58%,#f3cf71 100%)" };
const aviatorLogoCard = { background: "linear-gradient(135deg,#140608 0%,#2a0b11 35%,#471017 68%,#6f1823 100%)" };
const colorLogoCard = { background: "linear-gradient(135deg,#140f05 0%,#221906 42%,#45340f 74%,#6a5117 100%)" };
const gameLogoCardMobile = { ...quickActionMobile, padding: 0, borderRadius: "18px" };
const gameLogoCardLaptop = { ...quickActionLaptop, padding: "2px" };
const fullGameLogoImg = { width: "88%", height: "88%", objectFit: "contain", objectPosition: "center", display: "block", transform: "none", background: "transparent", mixBlendMode: "normal", filter: "drop-shadow(0 9px 12px rgba(15,23,42,.18))" };
const pngGameLogoImg = { ...fullGameLogoImg, width: "98%", height: "98%", padding: "0", objectFit: "contain", filter: "saturate(1.06) contrast(1.06) drop-shadow(0 10px 14px rgba(15,23,42,.22))" };
const dashboardWideLogoImg = { ...pngGameLogoImg, width: "100%", height: "100%", transform: "scale(1.01)" };
const dashboardColorLogoImg = { ...pngGameLogoImg, width: "100%", height: "100%", objectFit: "contain", borderRadius: "0", transform: "scale(1.01)", mixBlendMode: "normal", filter: "saturate(1.01) contrast(1.01) drop-shadow(0 7px 12px rgba(15,23,42,.12))" };
const aviatorGameLogoImg = { ...fullGameLogoImg, width: "100%", height: "100%", transform: "scale(1.01)", filter: "saturate(1.05) contrast(1.04) drop-shadow(0 10px 16px rgba(69,5,18,.24))" };
const disabledGameTile = { width: "100%", height: "100%", display: "grid", placeItems: "center", alignContent: "center", gap: "6px", color: "#9f1239" };
const disabledGameTitle = { fontSize: "15px", fontWeight: 900 };
const disabledGameNote = { fontSize: "11px", fontWeight: 800, padding: "3px 8px", borderRadius: "999px", background: "#ffe4e6", border: "1px solid #fecdd3" };
const sectionHeader = { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "2px 2px 0" };
const marketList = { display: "grid", gap: "12px" };
const marketListDesktop = { gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: "16px" };
const marketListLaptop = { gap: "12px" };
const marketCard = { background: "radial-gradient(circle at top right, rgba(255,255,255,.88), transparent 34%), linear-gradient(180deg, #f7fff8 0%, #fffdf1 100%)", borderRadius: "20px", padding: "12px 12px 0", boxShadow: "0 14px 30px rgba(34,197,94,0.14), inset 0 0 0 1px rgba(187,247,208,0.55)", border: "1px solid #bbf7d0", overflow: "hidden" };
const closedMarketCard = { ...marketCard, background: "linear-gradient(180deg, #f1f4f7 0%, #dde3e9 100%)", borderColor: "#c4ccd6", boxShadow: "0 8px 16px rgba(100,116,139,0.10)" };
const marketMainTap = { width: "100%", border: 0, background: "transparent", padding: 0, display: "grid", gap: "0", textAlign: "left", cursor: "pointer", position: "relative", overflow: "hidden" };
const marketGlossSweep = { position: "absolute", top: "-24%", bottom: "-24%", left: "-26%", width: "30%", transform: "skewX(-18deg)", background: "linear-gradient(90deg, rgba(255,255,255,0), rgba(255,255,255,.72), rgba(255,255,255,0))", opacity: 0, mixBlendMode: "screen", pointerEvents: "none", animation: "mkMarketGlossSweep 4.8s ease-in-out infinite", zIndex: 1 };
const marketTop = { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: "10px", marginBottom: "10px" };
const marketTitleWrap = { display: "flex", alignItems: "center", minWidth: 0 };
const marketHeadingWrap = { display: "grid", gap: "6px", minWidth: 0 };
const marketMeta = { display: "grid", justifyItems: "end", gap: "6px" };
const marketName = { fontSize: "16px", fontWeight: 900, color: "#1f2937", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "150px" };
const marketTime = { color: "#94a3b8", fontSize: "12px", fontWeight: 800 };
const openPill = { minHeight: "22px", padding: "0 10px", borderRadius: "999px", background: "linear-gradient(180deg,#f0fdf4,#dcfce7)", color: "#15803d", fontSize: "10px", fontWeight: 900, display: "inline-flex", alignItems: "center", width: "fit-content", border: "1px solid #86efac", boxShadow: "0 0 14px rgba(34,197,94,0.22)" };
const closedPill = { minHeight: "22px", padding: "0 10px", borderRadius: "999px", background: "#f1f5f9", color: "#64748b", fontSize: "10px", fontWeight: 900, display: "inline-flex", alignItems: "center", width: "fit-content", border: "1px solid #dbeafe" };
const resultRow = { display: "grid", gridTemplateColumns: "1fr 50px", alignItems: "center", gap: "0", minHeight: "60px", border: "1px solid rgba(134,239,172,.78)", borderRadius: "16px", background: "radial-gradient(circle at 88% 50%, rgba(34,197,94,.18), transparent 42%), linear-gradient(180deg, rgba(240,253,244,0.98) 0%, rgba(255,250,240,0.98) 100%)", padding: "0 8px 0 12px", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8), 0 0 18px rgba(34,197,94,.13)" };
const closedResultRow = { ...resultRow, background: "linear-gradient(180deg, #f8fafc 0%, #eef2f7 100%)", borderColor: "#d7dee8", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.8)" };
const resultBadge = { display: "inline-flex", alignItems: "center", minHeight: "36px", padding: "0 10px 0 0", borderRadius: "12px", fontWeight: 900, fontSize: "clamp(14px, 4vw, 17px)", letterSpacing: "0.08em", borderRight: "1px solid #e5e7eb", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" };
const placeholderTone = { color: "#94a3b8", background: "transparent" };
const realResultTone = { color: "#0f172a", background: "transparent" };
const playButton = { width: "40px", height: "40px", borderRadius: "13px", border: "1px solid #86efac", background: "linear-gradient(180deg, #4ade80 0%, #16a34a 100%)", color: "#f0fdf4", display: "grid", placeItems: "center", justifySelf: "end", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.35), 0 8px 16px rgba(22,163,74,0.28)" };
const disabledPlayButton = { ...playButton, borderColor: "#e5e7eb", background: "linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)", color: "#cbd5e1", boxShadow: "inset 0 1px 0 rgba(255,255,255,0.9)", cursor: "not-allowed", opacity: 1 };
const chartRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "8px", transform: "translateY(-3px)" };
const chartButtonBlue = { height: "38px", borderRadius: 12, border: "1px solid rgba(96,165,250,.28)", background: "linear-gradient(135deg,#eff6ff,#dbeafe)", color: "#2563eb", fontWeight: 900, fontSize: "12px", boxShadow: "0 8px 14px rgba(37,99,235,.10)" };
const chartButtonPurple = { ...chartButtonBlue, border: "1px solid rgba(196,181,253,.38)", background: "linear-gradient(135deg,#f5f3ff,#ede9fe)", color: "#7c3aed", boxShadow: "0 8px 14px rgba(124,58,237,.10)" };
const whatsappButton = { position: "fixed", right: "-8px", bottom: "92px", width: "118px", height: "118px", borderRadius: "50%", border: 0, background: "transparent", display: "grid", placeItems: "center", overflow: "visible", filter: "drop-shadow(0 18px 26px rgba(0,96,42,0.28))", zIndex: 80, boxSizing: "border-box", touchAction: "manipulation", cursor: "pointer" };
const whatsappGlyphWrap = { width: "114px", height: "114px", borderRadius: "50%", display: "grid", placeItems: "center", overflow: "visible" };
const pageNoticeBanner = { position: "fixed", top: "74px", left: "50%", transform: "translateX(-50%)", maxWidth: "min(92vw, 560px)", width: "fit-content", padding: "12px 18px", borderRadius: "16px", background: "linear-gradient(135deg,#fff7ed,#ffffff)", color: "#9a3412", border: "1px solid rgba(251,191,36,0.65)", boxShadow: "0 18px 38px rgba(30,64,175,0.12)", fontSize: "13px", fontWeight: 800, zIndex: 60, textAlign: "center" };
const dashboardDownloadCard = { width: "100%", minHeight: "74px", border: "1px solid rgba(134,239,172,.95)", borderRadius: "22px", background: "linear-gradient(135deg,#2cff8d 0%,#16d667 52%,#0ea54b 100%)", color: "#031b0d", display: "grid", alignContent: "center", justifyItems: "center", padding: "14px 18px", boxShadow: "0 0 0 1px rgba(187,247,208,.32) inset, 0 16px 28px rgba(16,185,129,.28), 0 0 20px rgba(34,255,136,.18)" };
const dashboardDownloadRow = { display: "flex", alignItems: "center", gap: "12px" };
const dashboardDownloadIconWrap = { width: "42px", height: "42px", borderRadius: "14px", background: "rgba(255,255,255,.94)", display: "grid", placeItems: "center", boxShadow: "0 10px 20px rgba(5,150,105,.20)", flexShrink: 0 };
const dashboardDownloadIconImg = { width: "24px", height: "24px", display: "block" };
const dashboardDownloadTitle = { fontSize: "18px", fontWeight: 900, color: "#031b0d", letterSpacing: "0.01em", whiteSpace: "nowrap" };
const dashboardTailSection = { display: "grid", gap: "12px", marginTop: "6px" };
const tailPromoCard = { width: "100%", minHeight: "88px", borderRadius: "22px", border: "1px solid rgba(250,204,21,.55)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "14px", padding: "16px 18px", textAlign: "left", boxShadow: "0 14px 30px rgba(15,23,42,.08)" };
const tailPromoBlue = { background: "linear-gradient(135deg,#fff4c2 0%,#fff9df 38%,#dbeafe 100%)" };
const tailPromoCopy = { display: "grid", gap: "4px" };
const tailPromoTitle = { fontSize: "20px", fontWeight: 900, color: "#b45309" };
const tailPromoSub = { fontSize: "13px", color: "#92400e", fontWeight: 700 };
const tailPromoBadge = { minWidth: "74px", height: "38px", borderRadius: "999px", background: "linear-gradient(135deg,#2563eb,#4f46e5)", color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "12px", fontWeight: 900, boxShadow: "0 10px 18px rgba(37,99,235,.18)" };
const tailTrustGrid = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" };
const tailTrustCard = { minHeight: "74px", borderRadius: "20px", background: "linear-gradient(180deg,#ffffff 0%,#f8fafc 100%)", border: "1px solid #e2e8f0", padding: "16px", display: "grid", gap: "5px", boxShadow: "0 8px 18px rgba(15,23,42,.05)" };
const tailTrustLabel = { fontSize: "16px", fontWeight: 900, color: "#2563eb" };
const tailTrustValue = { fontSize: "12px", color: "#64748b", fontWeight: 800 };

export default Dashboard;
